import React from "react";
import { useTranslation } from "react-i18next";
import {
  HailingStepData,
  hasMultipleVehicleClasses,
  selectedVehicleClasses,
} from "../mock/data";
import {
  HailingOrderSnapshot,
  MeHailingOrderService,
} from "../../../api/meHailingOrder.service";
import { ReceiptService } from "../../../api/receipt.service";
import { getDashErrorCode } from "../../FlightAlerts/handler/apiError.helper";
import CancellationPolicySheet from "../../web-receipt/CancellationPolicySheet";
import { Receipt } from "../../../models/receipt/receipt";
import { driverRoutePolyline } from "../driverRoute";
import { DriverHeartbeat, useHailRealtime } from "../hooks/useHailRealtime";
import TripDriverCard from "../tracker/TripDriverCard";
import TripPlacesCard from "../tracker/TripPlacesCard";
import TripStatusCard from "../tracker/TripStatusCard";
import {
  getPickupTime,
  getTripSubtitle,
  getTripTimeInfo,
  getTripTitle,
  toTripPhase,
} from "../tracker/tripStatus.helper";

// The rider socket pushes each order change and driver position. Polling stays as a safety
// net: slow while the socket is connected, at the old 10s rate while it is not.
const REFRESH_MS = 10_000;
const SOCKET_REFRESH_MS = 60_000;
const TERMINAL_STATUSES = ["COMPLETED", "CANCELLED", "TIMED_OUT"];

export interface RideTrackerProps {
  state: HailingStepData;
  onCancelTrip?: () => void;
  onMapUpdate?: (
    routePolyline: string | null | undefined,
    driverPosition: { lat: number; lng: number } | null | undefined,
    driverOperatingArea: string | null | undefined,
  ) => void;
  /** The last status known for this order, shown until the first refresh lands. */
  initialStatus?: string | undefined;
  /** The order's latest status (upper case), each time it is refreshed. */
  onStatusChange?: (status: string) => void;
  isCancelling?: boolean;
  cancelError?: boolean;
  /** The rider cancelled this ride: show it as cancelled until they book again. */
  isCancelled?: boolean;
  onBookAgain?: () => void;
}

const RideTracker: React.FC<RideTrackerProps> = ({
  state,
  onCancelTrip,
  onMapUpdate,
  onStatusChange,
  initialStatus,
  isCancelling = false,
  cancelError = false,
  isCancelled = false,
  onBookAgain,
}) => {
  const { t, i18n } = useTranslation("hailing");
  // Cancel flow, as on the web receipt: check the fee, confirm, then cancel.
  // The fee is null when it could not be checked.
  const [checkingFee, setCheckingFee] = React.useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = React.useState(false);
  const [cancellationFee, setCancellationFee] = React.useState<number | null>(
    null,
  );
  const [policyOpen, setPolicyOpen] = React.useState(false);
  const [orderSnapshot, setOrderSnapshot] =
    React.useState<HailingOrderSnapshot | null>(null);
  const [statusRefreshFailed, setStatusRefreshFailed] = React.useState(false);
  // The hailing order API has no driver ETA, driver route or live position yet; the
  // web receipt's API does, for the same request id.
  const [receipt, setReceipt] = React.useState<Receipt | null>(null);
  const tripEndsRef = React.useRef({
    pickup:
      state.pickupLat != null && state.pickupLng != null
        ? { lat: state.pickupLat, lng: state.pickupLng }
        : null,
    dropoff:
      state.dropoffLat != null && state.dropoffLng != null
        ? { lat: state.dropoffLat, lng: state.dropoffLng }
        : null,
  });
  // A boost is added on the server and cannot be taken back, so a tap only asks first:
  // `pendingBoost` is the amount awaiting the rider's confirmation.
  const [pendingBoost, setPendingBoost] = React.useState<number | null>(null);
  const [isBoosting, setIsBoosting] = React.useState(false);
  const [boostError, setBoostError] = React.useState("");
  const [customBoostInput, setCustomBoostInput] = React.useState("");
  const [isCustomBoostOpen, setIsCustomBoostOpen] = React.useState(false);
  const [customBoostError, setCustomBoostError] = React.useState("");
  const money = React.useMemo(
    () =>
      new Intl.NumberFormat("en-HK", {
        style: "currency",
        currency: "HKD",
        maximumFractionDigits: 2,
      }),
    [],
  );
  const baseAmount =
    state.fareKind === "meter"
      ? state.vehicleClass.meter
      : state.vehicleClass.flat;
  // The boost the order carries, as the server last reported it: on the bill once there is
  // one, and on the pending fare range (`billing` is null) until then.
  const currentBoost =
    orderSnapshot?.billing?.boostAmount ??
    orderSnapshot?.matching?.minMaxFareCalculations?.[0]?.boostAmount ??
    0;
  const isMultipleClasses = hasMultipleVehicleClasses(state);
  const vehicleClassLabel = `${selectedVehicleClasses(state)
    .map((vehicle) => vehicle.name)
    .join(" / ")} · ${t(
    state.fareKind === "meter" ? "steps.prices.meter" : "steps.prices.flat",
  )}`;
  // Same brand logos as the web receipt's payment card.
  const cardBrand = state.paymentMethod.name.toUpperCase();
  const cardBrandIcon = ["VISA", "MASTERCARD", "UNIONPAY"].includes(cardBrand)
    ? `/icons8-${cardBrand.toLowerCase()}.svg`
    : null;
  const cardLabel = state.paymentMethod.sub
    ? `${state.paymentMethod.name} ${state.paymentMethod.sub}`
    : state.paymentMethod.name;
  const orderStatus = isCancelled
    ? "CANCELLED"
    : (orderSnapshot?.status.toUpperCase() ?? initialStatus ?? "PENDING");
  const isTerminalOrderStatus = ["COMPLETED", "CANCELLED", "TIMED_OUT"].includes(
    orderStatus,
  );
  // A completed trip shows what was charged: the order's `userTotal` (= `billing.total`, meter
  // fare + boost + fees). Not `billing.tripTotal`, which is the meter fare alone, and the
  // receipt's `total` only as a last resort, as it can still be the booking estimate. Until
  // then it is the server's current total (boost included), or the booked fare plus boost
  // before that arrives.
  const finalTotal =
    orderStatus === "COMPLETED"
      ? (orderSnapshot?.userTotal ?? orderSnapshot?.billing?.total ?? receipt?.total ?? null)
      : null;
  const currentTotal = orderSnapshot?.userTotal ?? baseAmount + currentBoost;
  const estimateLabel = `${state.fareKind === "meter" ? `${t("steps.prices.est")} ` : ""}${money.format(currentTotal)}`;
  // Until a driver accepts, several classes mean the fare is "up to" the highest of them.
  const priceLabel =
    finalTotal !== null
      ? money.format(finalTotal)
      : isMultipleClasses && orderStatus === "PENDING"
        ? t("steps.prices.upTo", { amount: estimateLabel })
        : estimateLabel;
  const headingKey: Record<string, string> = {
    ACCEPTED: "acceptedTitle",
    APPROACHING: "approachingTitle",
    ARRIVED: "arrivedTitle",
    ON_GOING: "ongoingTitle",
    COMPLETED: "completedTitle",
    CANCELLED: "cancelledTitle",
    TIMED_OUT: "timedOutTitle",
  };
  const subtitleKey: Record<string, string> = {
    ACCEPTED: "acceptedSubtitle",
    APPROACHING: "approachingSubtitle",
    ARRIVED: "arrivedSubtitle",
    ON_GOING: "ongoingSubtitle",
  };
  const orderHeading = t(
    `steps.ride.finding.${headingKey[orderStatus] ?? "title"}`,
  );
  const orderSubtitle =
    subtitleKey[orderStatus] || orderStatus === "PENDING"
      ? t(`steps.ride.finding.${subtitleKey[orderStatus] ?? "subtitle"}`)
      : null;
  // As on the web receipt, a ride can be cancelled until the trip starts.
  const canCancel =
    Boolean(onCancelTrip) && !isTerminalOrderStatus && orderStatus !== "ON_GOING";
  const driverName =
    i18n.language.startsWith("zh")
      ? orderSnapshot?.driver?.nameTc || orderSnapshot?.driver?.name
      : orderSnapshot?.driver?.name || orderSnapshot?.driver?.nameTc;
  const vehicleName =
    i18n.language.startsWith("zh")
      ? orderSnapshot?.vehicle?.displayNameTc ||
        orderSnapshot?.vehicle?.displayName
      : orderSnapshot?.vehicle?.displayName ||
        orderSnapshot?.vehicle?.displayNameTc;
  const showDriverCard =
    Boolean(orderSnapshot?.driver || orderSnapshot?.vehicle) &&
    orderStatus !== "PENDING" &&
    !isTerminalOrderStatus;
  // Once a driver is involved, the header follows the web receipt's status card, built
  // from the receipt API with the receipt's wording (status, ETA, arrival time).
  const tripStatusCard = React.useMemo(() => {
    if (!receipt || orderStatus === "PENDING") return null;
    const phase = toTripPhase(receipt.status);
    // Just cancelled here: wait until the receipt says so too.
    if (isCancelled && phase !== "cancelled") return null;
    const lang = i18n.language.startsWith("zh") ? "zhHK" : "en";
    const placeName = (index: number) =>
      receipt.tripItinerary?.[index]?.i18n?.[lang]?.displayName ||
      receipt.tripItinerary?.[index]?.i18n?.zhHK?.displayName ||
      "";
    return {
      phase,
      title: getTripTitle(t, phase, {
        time: receipt.time,
        etaSeconds: receipt.eta,
        rawStatus: receipt.status,
        pickupName: placeName(0),
        dropoffName: placeName(1),
      }),
      subtitle: getTripSubtitle(t, phase, {
        updatedAt: receipt.updatedAt,
        rawStatus: receipt.status,
      }),
      timeInfo: getTripTimeInfo(t, phase, { time: receipt.time, etaSeconds: receipt.eta }),
      showDriverLocationBanner: ["ACCEPTED", "CHECK_IN"].includes(receipt.status),
      isArrived: receipt.status === "ARRIVED",
    };
  }, [i18n.language, isCancelled, orderStatus, receipt, t]);

  // Set by the refresh effect below, so socket frames can reach the running refresh loop.
  const refreshNowRef = React.useRef<() => void>(() => undefined);
  const socketConnectedRef = React.useRef(false);
  // What the map last showed, so a driver pin from the socket can move it without a refetch.
  const lastMapRef = React.useRef<{
    status: string;
    shape: string | null | undefined;
    route: string | null | undefined;
    operatingArea: string | null | undefined;
  } | null>(null);
  const onMapUpdateRef = React.useRef(onMapUpdate);
  onMapUpdateRef.current = onMapUpdate;
  // Through refs, so a parent re-creating these callbacks does not restart the refresh loop
  // (which would clear the order on screen and read it again).
  const onStatusChangeRef = React.useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  React.useEffect(() => {
    // A new status callback (e.g. the parent now knows which trip this is) still hears the
    // status already on screen.
    const last = lastMapRef.current;
    if (last) onStatusChange?.(last.status);
  }, [onStatusChange]);

  const applyDriverHeartbeat = React.useCallback((driverPosition: DriverHeartbeat) => {
    const last = lastMapRef.current;
    if (!last || TERMINAL_STATUSES.includes(last.status)) return;
    const route =
      driverRoutePolyline({
        status: last.status,
        shape: last.shape,
        driverPosition,
        ...tripEndsRef.current,
      }) ?? last.route;
    onMapUpdateRef.current?.(route, driverPosition, last.operatingArea);
  }, []);

  const socketConnected = useHailRealtime(state.hailingRequestId, {
    onOrderChanged: () => refreshNowRef.current(),
    onDriverHeartbeat: applyDriverHeartbeat,
  });
  socketConnectedRef.current = socketConnected;

  React.useEffect(() => {
    // Read the order again once the socket is up, in case a change landed before it connected.
    if (socketConnected) refreshNowRef.current();
  }, [socketConnected]);

  React.useEffect(() => {
    const hailingRequestId = state.hailingRequestId;
    if (!hailingRequestId) return;

    let active = true;
    let requestTimer: ReturnType<typeof setTimeout> | undefined;
    let hasLoggedRefreshError = false;
    let inFlight = false;
    let refreshAgain = false;
    let finished = false;
    const orderService = new MeHailingOrderService();
    const receiptService = new ReceiptService();

    const scheduleRefresh = () => {
      if (requestTimer !== undefined) clearTimeout(requestTimer);
      requestTimer = setTimeout(
        refreshOrder,
        socketConnectedRef.current ? SOCKET_REFRESH_MS : REFRESH_MS,
      );
    };

    const refreshOrder = async () => {
      if (inFlight) {
        refreshAgain = true;
        return;
      }
      inFlight = true;
      try {
        const snapshot = await orderService.getOrder(hailingRequestId);
        if (!active) return;
        const status = snapshot.status.toUpperCase();
        let route = snapshot.tripEstimation?.encodedPolyline;
        let driverPosition = snapshot.live?.driverLocation;
        let latestReceipt: Receipt | null = null;
        if (status !== "PENDING") {
          try {
            const { data: receipt } = await receiptService.getReceipt(hailingRequestId);
            if (!active) return;
            latestReceipt = receipt;
            // The receipt's heartbeat is the driver's latest position; the order API's
            // live.driverLocation can lag well behind it, so the heartbeat wins.
            const heartBeat = receipt.driver?.heartBeat;
            if (heartBeat) driverPosition = { lat: heartBeat.lat, lng: heartBeat.lng };
            route =
              driverRoutePolyline({
                status,
                shape: receipt.driver?.shape,
                driverPosition,
                ...tripEndsRef.current,
              }) ?? route;
          } catch {
            // Best effort: without the receipt the map keeps the trip route and the
            // header falls back to the plain status heading.
          }
        }
        lastMapRef.current = {
          status,
          shape: latestReceipt?.driver?.shape,
          route: snapshot.tripEstimation?.encodedPolyline,
          operatingArea: snapshot.driver?.operatingArea,
        };
        setOrderSnapshot(snapshot);
        setReceipt(latestReceipt);
        setStatusRefreshFailed(false);
        onMapUpdateRef.current?.(route, driverPosition, snapshot.driver?.operatingArea);
        onStatusChangeRef.current?.(status);
        hasLoggedRefreshError = false;
        finished = TERMINAL_STATUSES.includes(status);
        if (!finished) scheduleRefresh();
      } catch (error) {
        if (!active) return;
        setStatusRefreshFailed(true);
        if (!hasLoggedRefreshError) {
          console.error("Failed to refresh hailing order status.", error);
          hasLoggedRefreshError = true;
        }
        scheduleRefresh();
      } finally {
        inFlight = false;
        if (active && refreshAgain && !finished) {
          refreshAgain = false;
          void refreshOrder();
        }
      }
    };

    refreshNowRef.current = () => {
      if (!active || finished) return;
      if (requestTimer !== undefined) clearTimeout(requestTimer);
      void refreshOrder();
    };

    setOrderSnapshot(null);
    setStatusRefreshFailed(false);
    lastMapRef.current = null;
    void refreshOrder();

    return () => {
      active = false;
      refreshNowRef.current = () => undefined;
      if (requestTimer !== undefined) clearTimeout(requestTimer);
    };
  }, [state.hailingRequestId]);

  const askBoost = (amount: number) => {
    setPendingBoost(amount);
    setIsCustomBoostOpen(false);
    setCustomBoostError("");
    setBoostError("");
  };

  const applyCustomBoost = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const amount = Number(customBoostInput);
    if (!Number.isFinite(amount) || amount <= 0) {
      setCustomBoostError(t("steps.ride.finding.invalidCustomBoost"));
      return;
    }
    askBoost(amount);
  };

  const confirmBoost = async () => {
    const hailingRequestId = state.hailingRequestId;
    if (pendingBoost === null || !hailingRequestId) return;
    setIsBoosting(true);
    setBoostError("");
    try {
      setOrderSnapshot(
        await new MeHailingOrderService().boostOrder(hailingRequestId, pendingBoost),
      );
      setCustomBoostInput("");
    } catch (error) {
      console.error("Failed to boost the hailing order.", error);
      const code = getDashErrorCode(error);
      setBoostError(
        t(
          code === "HL_002"
            ? "steps.ride.finding.boostLimitError"
            : code === "HL_003"
              ? "steps.ride.finding.boostTooLate"
              : "steps.ride.finding.boostError",
        ),
      );
    } finally {
      setPendingBoost(null);
      setIsBoosting(false);
    }
  };

  const openCancelConfirm = async () => {
    const hailingRequestId = state.hailingRequestId;
    if (!hailingRequestId) return;
    setCheckingFee(true);
    try {
      setCancellationFee(
        await new MeHailingOrderService().getCancellationFee(hailingRequestId),
      );
    } catch (error) {
      console.error("Failed to check the hailing cancellation fee.", error);
      setCancellationFee(null);
    } finally {
      setCheckingFee(false);
    }
    setCancelConfirmOpen(true);
  };

  const confirmCancel = () => {
    setCancelConfirmOpen(false);
    onCancelTrip?.();
  };

  const cancelConfirmText =
    cancellationFee === null
      ? t("steps.ride.finding.cancelModalUnknownFee")
      : cancellationFee === 0
        ? t("steps.ride.finding.cancelModalFree")
        : t("steps.ride.finding.cancelModalPaid", { fee: cancellationFee });

  return (
    <div className="flex flex-1 flex-col">
      {tripStatusCard ? (
        <div className="overflow-hidden rounded-lg">
          <TripStatusCard {...tripStatusCard} />
        </div>
      ) : (
        <>
          <div className="text-[26px] font-bold tracking-[-0.5px] text-ink-100">
            {orderHeading}
          </div>
          {orderSubtitle ? (
            <div className="mt-1 text-[14px] text-[#3A4045]">
              {orderSubtitle}
            </div>
          ) : null}
        </>
      )}
      {orderStatus === "PENDING" ? (
        <div className="relative mt-4 h-1 w-full overflow-hidden rounded-full bg-[#E6F7FF]">
          <div
            aria-hidden="true"
            className="absolute bottom-0 top-0 w-[45%] rounded-full bg-[#2196F2]"
            style={{ animation: "hkbar 1.6s ease-in-out infinite" }}
          />
        </div>
      ) : null}
      {/* A boost only helps find a driver, so it is offered until one accepts. */}
      {orderStatus !== "PENDING" ? null : (
        <section className="mt-5 rounded-[20px] bg-[#E8E9EA] p-4">
          <h2 className="text-[16px] font-semibold text-ink-100">
            {t("steps.ride.finding.boostTitle")}
          </h2>
          <p className="mt-0.5 text-[12px] text-[#6B7075]">
            {t("steps.ride.finding.boostNote")}
          </p>
          <div className="mt-3 flex gap-2">
            {[10, 20, 30].map((amount) => (
              <button
                key={amount}
                type="button"
                disabled={isBoosting || pendingBoost !== null}
                onClick={() => askBoost(amount)}
                className="h-[42px] flex-1 rounded-[21px] border border-[#D5D7D9] bg-white text-[15px] font-medium text-ink-100 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                +${amount}
              </button>
            ))}
            <button
              type="button"
              aria-expanded={isCustomBoostOpen}
              disabled={isBoosting || pendingBoost !== null}
              onClick={() => {
                setIsCustomBoostOpen((open) => !open);
                setCustomBoostError("");
              }}
              className={`h-[42px] flex-[1.3] rounded-[21px] border border-[#D5D7D9] text-[15px] font-medium disabled:cursor-not-allowed disabled:opacity-50 ${
                isCustomBoostOpen
                  ? "bg-ink-100 text-white"
                  : "bg-white text-ink-100"
              }`}
            >
              {t("steps.ride.finding.custom")}
            </button>
          </div>
          {isCustomBoostOpen ? (
            <form
              onSubmit={applyCustomBoost}
              className="mt-3 flex items-end gap-2"
            >
              <label className="flex-1 text-[12px] font-medium text-[#3A4045]">
                {t("steps.ride.finding.customAmount")}
                <input
                  aria-label={t("steps.ride.finding.customAmount")}
                  type="number"
                  min="1"
                  step="1"
                  value={customBoostInput}
                  onChange={(event) => setCustomBoostInput(event.target.value)}
                  className="mt-1 h-10 w-full rounded-lg border border-[#D5D7D9] bg-white px-3 text-[14px] text-ink-100"
                />
              </label>
              <button
                type="submit"
                className="h-10 rounded-lg bg-brand-blue px-4 text-[13px] font-semibold text-white"
              >
                {t("steps.ride.finding.applyBoost")}
              </button>
            </form>
          ) : null}
          {pendingBoost !== null ? (
            <div
              role="group"
              aria-label={t("steps.ride.finding.boostConfirmTitle", {
                amount: money.format(pendingBoost),
              })}
              className="mt-3 rounded-xl bg-white p-3"
            >
              <p className="text-[14px] font-semibold text-ink-100">
                {t("steps.ride.finding.boostConfirmTitle", {
                  amount: money.format(pendingBoost),
                })}
              </p>
              <p className="mt-0.5 text-[12px] leading-[1.4] text-[#6B7075]">
                {t("steps.ride.finding.boostConfirmBody", {
                  total: money.format(currentBoost + pendingBoost),
                })}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setPendingBoost(null)}
                  disabled={isBoosting}
                  className="h-10 flex-1 rounded-lg border border-[#D5D7D9] bg-white text-[13px] font-semibold text-ink-100 disabled:opacity-50"
                >
                  {t("steps.ride.finding.boostCancel")}
                </button>
                <button
                  type="button"
                  onClick={() => void confirmBoost()}
                  disabled={isBoosting}
                  className="h-10 flex-1 rounded-lg bg-brand-blue text-[13px] font-semibold text-white disabled:cursor-wait disabled:opacity-70"
                >
                  {isBoosting
                    ? t("steps.ride.finding.boosting")
                    : t("steps.ride.finding.boostConfirm")}
                </button>
              </div>
            </div>
          ) : null}
          {customBoostError || boostError ? (
            <p role="alert" className="mt-2 text-[12px] text-[#A12622]">
              {customBoostError || boostError}
            </p>
          ) : null}
          {currentBoost > 0 ? (
            <p role="status" className="mt-2 text-[12px] font-medium text-ink-100">
              {t("steps.ride.finding.boostAdded", { amount: money.format(currentBoost) })}
            </p>
          ) : null}
        </section>
      )}
      {/* Driver and itinerary cards follow the web receipt's (our own copies, see tracker/).
          They carry the receipt's side margins; -mx-5 lines them up with this panel. */}
      {showDriverCard && orderSnapshot ? (
        <section aria-label={t("steps.ride.finding.driverDetails")} className="-mx-5 mt-2">
          <TripDriverCard
            licensePlate={orderSnapshot.vehicle?.licensePlate ?? ""}
            carMake={orderSnapshot.vehicle?.make ?? ""}
            carModel={vehicleName || orderSnapshot.vehicle?.model || ""}
            driverName={driverName ?? ""}
            driverPhone={orderSnapshot.driver?.phoneNumber ?? ""}
            carImageUrl={orderSnapshot.vehicle?.imageUrl ?? ""}
            vehicleClassLabel={vehicleClassLabel}
          />
        </section>
      ) : null}
      {state.pickupPlace || state.dropoffPlace ? (
        <div className="-mx-5">
          <TripPlacesCard
            pickupTime={getPickupTime(t, orderSnapshot?.request?.time)}
            pickupName={state.pickupPlace}
            pickupAddress={state.pickupAddress}
            dropoffName={state.dropoffPlace}
            dropoffAddress={state.dropoffAddress}
          />
        </div>
      ) : null}
      <div className="mt-2 flex items-center gap-3.5 rounded-[18px] bg-white px-3.5 py-3">
        {cardBrandIcon ? (
          <img
            src={cardBrandIcon}
            alt={state.paymentMethod.name}
            className="h-6 w-6 shrink-0 object-contain"
          />
        ) : (
          <span className="font-material-symbols text-[22px] leading-none text-[#8A8F93]">
            credit_card
          </span>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[15px] font-medium text-ink-100">
            {cardBrandIcon && state.paymentMethod.sub
              ? state.paymentMethod.sub
              : cardLabel}
          </span>
          {/* Until a driver card shows it, the ride class stays with the fare. */}
          {showDriverCard ? null : (
            <span className="text-[12px] text-[#6B7075]">{vehicleClassLabel}</span>
          )}
        </div>
        <span className="text-[15px] font-semibold text-brand-blue">
          {priceLabel}
        </span>
      </div>
      {state.hailingRequestId ? (
        <p className="mt-3 text-[12px] text-[#6B7075]">
          {t("steps.ride.finding.requestReference", {
            id: state.hailingRequestId,
          })}
        </p>
      ) : null}
      {!orderSnapshot || statusRefreshFailed ? (
        <p role="status" className="mt-3 text-[12px] text-[#6B7075]">
          {statusRefreshFailed
            ? t("steps.ride.finding.statusRefreshFailed")
            : t("steps.ride.finding.statusLoading")}
        </p>
      ) : null}
      {cancelError ? (
        <p role="alert" className="mt-3 text-[12px] text-[#A12622]">
          {t("steps.ride.finding.cancelError")}
        </p>
      ) : null}
      {isCancelled ? (
        <div className="mt-auto pt-5">
          {cancellationFee ? (
            <div className="flex items-center justify-between rounded-[18px] bg-white px-4 py-3 text-[15px] font-semibold text-ink-100">
              <span>{t("steps.ride.finding.cancellationFee")}</span>
              <span>{`$${cancellationFee}`}</span>
            </div>
          ) : null}
          <button
            type="button"
            onClick={onBookAgain}
            className="mt-3 h-[52px] w-full rounded-[16px] bg-brand-blue text-[16px] font-semibold text-white"
          >
            {t("steps.ride.finding.bookAgain")}
          </button>
        </div>
      ) : canCancel ? (
        <div className="mt-auto pt-5">
          <button
            type="button"
            onClick={openCancelConfirm}
            disabled={isCancelling || checkingFee}
            className="h-[52px] w-full rounded-[16px] border-[1.5px] border-[#D6336C] text-[16px] font-semibold text-[#D6336C] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isCancelling
              ? t("steps.ride.finding.cancellingTrip")
              : checkingFee
                ? t("steps.ride.finding.checkingCancellationFee")
                : t("steps.ride.finding.cancelTrip")}
          </button>
          <p className="mt-3 text-[12px] leading-[1.4] text-[#6B7075]">
            {t("steps.ride.finding.cancelFeeNote")}{" "}
            <button
              type="button"
              onClick={() => setPolicyOpen(true)}
              className="font-medium text-brand-blue"
            >
              {t("steps.ride.finding.cancelFeeNoteLink")}
            </button>
          </p>
        </div>
      ) : null}

      <CancellationPolicySheet
        isOpen={policyOpen}
        onClose={() => setPolicyOpen(false)}
      />

      {cancelConfirmOpen ? (
        <div
          onClick={() => setCancelConfirmOpen(false)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="hailing-cancel-title"
            onClick={(event) => event.stopPropagation()}
            className="relative flex min-w-[350px] max-w-[90vw] flex-col gap-6 rounded-2xl bg-white p-8"
          >
            <button
              type="button"
              onClick={() => setCancelConfirmOpen(false)}
              aria-label={t("support.close")}
              className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
            >
              <span className="text-2xl font-bold">&times;</span>
            </button>
            <div
              id="hailing-cancel-title"
              className="mb-2 text-2xl font-bold text-gray-900"
            >
              {t("steps.ride.finding.cancelModalTitle")}
            </div>
            <div className="mb-2 text-lg text-gray-700">
              {cancelConfirmText}
            </div>
            <button
              type="button"
              onClick={() => setCancelConfirmOpen(false)}
              className="w-full rounded-full bg-blue-600 py-3 text-lg font-semibold text-white hover:bg-blue-700"
            >
              {t("steps.ride.finding.keepOrder")}
            </button>
            <button
              type="button"
              onClick={confirmCancel}
              className="w-full rounded-full bg-gray-100 py-3 text-lg font-semibold text-red-500 hover:bg-red-100"
            >
              {t("steps.ride.finding.cancelOrder")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RideTracker;
